/**
 * Copyright Highway9 Networks Inc.
 */

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { serviceInstanceService } from "~/services";
import {
  ServiceKpiMetrics,
  ServiceKpiQuery,
  ServiceKpiFilters,
  TenantType,
  AggregationMode,
  TenantMetricData,
  ServiceKpiDisplayMetricKey,
  ServiceKpiResolutionPeriod,
} from "~/types/serviceKpi";
import moment from "moment-timezone";
import {
  AggregateMetricResponse,
  buildAggregateFromMetrics,
  buildQueryKeys,
  buildTenantInfoById,
  emptyAggregate,
  getKpiFetchFormulas,
  getResolutionSeconds,
} from "~/views/serviceKpis/utils";

interface ServiceKpiState {
  metrics: ServiceKpiMetrics | null;
  filters: ServiceKpiFilters;
  loading: boolean;
  error: string | null;
}

const initialState: ServiceKpiState = {
  metrics: null,
  filters: {
    tenantType: "all",
    selectedTenantIds: [],
    viewMode: "all",
    managedBy: "all",
    selectedVmcId: "all",
    startTime: moment().subtract(5, 'years').unix(),
    endTime: moment().unix(),
    interval: "5year",
    selectedGrowthMetric: "vmc",
    resolutionPeriod: "1quarter",
  },
  loading: false,
  error: null,
};

/**
 * Fetch Service KPI metrics
 */
export const fetchServiceKpiMetrics = createAsyncThunk(
  "serviceKpi/fetchMetrics",
  async (query: ServiceKpiQuery, { rejectWithValue, getState }) => {
    try {
      // Get service instances from state or fetch them
      const state = getState() as RootState;
      let serviceInstances = state.serviceInstance.data;
      
      // If service instances are not available, fetch them
      if (serviceInstances?.length === 0) {
        serviceInstances = await serviceInstanceService.getServiceInstances();
      }

      // Prepare the list of tenants, falling back to empty array if no service instances
      const tenants = serviceInstances || [];

      // Build a Map of tenant info keyed by tenant ID for later reference
      const tenantInfoById = buildTenantInfoById(tenants);

      // Determine start and end timestamps from query or default to 0
      const { start, end } = { start: query.startTime ?? 0, end: query.endTime ?? 0 };

      // Clean the list of filter tenant IDs, filtering out null/undefined
      const tenantFilterIds = (query.tenantIds || []).filter(Boolean);
      const effectiveTenantType = query.tenantType ?? "all";

      // Build the aggregation query keys and a flag indicating if there will be no results
      const { queryKeys, noResults } = buildQueryKeys(
        tenants,
        tenantFilterIds,
        (query.managedBy ?? "all") as "all" | "highway9" | string,
        query.selectedVmcId ?? "all"
      );

      // If no results would be returned with current filters, return an empty aggregate object
      if (noResults) {
        return { aggregate: emptyAggregate() };
      }

      // Resolves the requested aggregation period ('1week' | '1month') to a bucket size in seconds.
      const resolutionSeconds = getResolutionSeconds(query.resolutionPeriod);

      // Always request all extension formulas; VMC count uses effective Mobile Cloud Type.
      const metricsPayload = {
        metrics: getKpiFetchFormulas(effectiveTenantType, query.resolutionPeriod),
        interval: {
          startTime: query.startTime ?? 0,
          endTime: query.endTime ?? 0,
        },
        resolution: resolutionSeconds,
        queryKeys, // Aggregated query keys
      };

      // Fetch aggregate data for KPI metrics across the entire filtered set
      const aggregateSeries = (await serviceInstanceService.getMetrics(metricsPayload)) as AggregateMetricResponse[];
      // Convert the metrics API response into our aggregate object within the date range
      const aggregate = buildAggregateFromMetrics(aggregateSeries, start, end, resolutionSeconds);

      // Start building response object with aggregate data
      const result: ServiceKpiMetrics = {
        aggregate,
      };

      // If the request is for per-customer aggregation and specific tenants are selected
      if (query.aggregation === "per-customer" && tenantFilterIds.length > 0) {
        const customers: Record<string, TenantMetricData> = {};

        // Fetch metrics for each filtered tenant in parallel
        const perTenantSeries = await Promise.all(
          tenantFilterIds.map(async (tenantId) => {
            const tenantPayload = {
              ...metricsPayload,
              metrics: getKpiFetchFormulas(effectiveTenantType, query.resolutionPeriod),
              queryKeys: [
                ...(metricsPayload.queryKeys || []).filter((qk: any) => qk.key !== "tenantId"),
                { key: "tenantId", value: tenantId },
              ],
            };
            // Fetch series for each tenant
            const series = (await serviceInstanceService.getMetrics(tenantPayload)) as AggregateMetricResponse[];
            // Return tenant ID and their computed aggregate
            return [tenantId, buildAggregateFromMetrics(series, start, end, resolutionSeconds)] as const;
          })
        );

        // Assemble the results per tenant, adding tenant info and all metrics
        for (const [tenantId, tenantAggregate] of perTenantSeries) {
          const tenantInfo = tenantInfoById.get(tenantId);
          customers[tenantId] = {
            name: tenantInfo?.name || tenantId,
            tenantType: tenantInfo?.tenantType || "EX",
            vmc: tenantAggregate.vmc,
            edges: tenantAggregate.edges,
            radios: tenantAggregate.radios,
            devices: tenantAggregate.devices,
            sqft: tenantAggregate.sqft,
            indoorSqft: tenantAggregate.indoorSqft,
            outdoorSqft: tenantAggregate.outdoorSqft,
            sites: tenantAggregate.sites,
            devicesConnected: tenantAggregate.devicesConnected,
            uniqueDevices: tenantAggregate.uniqueDevices,
            uploadTonnage: tenantAggregate.uploadTonnage,
            downloadTonnage: tenantAggregate.downloadTonnage,
            uploadTonnageEX: tenantAggregate.uploadTonnageEX,
            downloadTonnageEX: tenantAggregate.downloadTonnageEX,
            volteCalls: tenantAggregate.volteCalls,
            peakConnections: tenantAggregate.peakConnections,
          };
        }
        // Attach tenants breakdown to result
        result.tenants = customers;
      }

      // Return the assembled result, including aggregate and optionally tenants
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch service KPI metrics");
    }
  }
);

const serviceKpiSlice = createSlice({
  name: "serviceKpi",
  initialState,
  reducers: {
    setTenantType: (state, action: PayloadAction<'all' | TenantType>) => {
      state.filters.tenantType = action.payload;
    },
    setSelectedTenantIds: (state, action: PayloadAction<string[]>) => {
      state.filters.selectedTenantIds = action.payload;
    },
    setViewMode: (state, action: PayloadAction<AggregationMode>) => {
      state.filters.viewMode = action.payload;
    },
    setManagedBy: (state, action: PayloadAction<string>) => {
      state.filters.managedBy = action.payload;
    },
    setSelectedVmcId: (state, action: PayloadAction<string>) => {
      state.filters.selectedVmcId = action.payload;
    },
    setDateRange: (state, action: PayloadAction<{ startTime?: number; endTime?: number; interval?: string }>) => {
      state.filters.startTime = action.payload.startTime ?? 0;
      state.filters.endTime = action.payload.endTime ?? 0;
      state.filters.interval = action.payload.interval ?? "custom";
    },
    setSelectedGrowthMetric: (state, action: PayloadAction<ServiceKpiDisplayMetricKey>) => {
      state.filters.selectedGrowthMetric = action.payload;
    },
    setResolutionPeriod: (state, action: PayloadAction<ServiceKpiResolutionPeriod>) => {
      state.filters.resolutionPeriod = action.payload;
    },
    setFilters: (state, action: PayloadAction<ServiceKpiFilters>) => {
      state.filters = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch metrics
    builder
      .addCase(fetchServiceKpiMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServiceKpiMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.metrics = action.payload;
      })
      .addCase(fetchServiceKpiMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const serviceKpiActions = serviceKpiSlice.actions;
export default serviceKpiSlice.reducer;

// Selectors
export const selectServiceKpiMetrics = (state: RootState) => state.serviceKpi.metrics;
export const selectServiceKpiFilters = (state: RootState) => state.serviceKpi.filters;
export const selectServiceKpiLoading = (state: RootState) => state.serviceKpi.loading;
export const selectServiceKpiError = (state: RootState) => state.serviceKpi.error;

